<script setup>

import { defineProps, defineEmits, computed, ref, watch} from "vue";
import BarraNavegacao from "./BarraNavegacao.vue";
import DadoConteudo from "./DadoConteudo.vue";
import UtilNumber from "@/utils/UtilNumber";

//==================================================================//
//========================= Props & Emits ==========================//
//==================================================================//

const props = defineProps([
    "usuarioStore",
    "colaboradorStore",
    "controlesDesativados",
    "gerandoRecebimentos",
    "contasFinanceiras",
    "dadoOriginal",
]);

const emits = defineEmits([
    "update:alteraControlesDesativados",
    "update:alteraGerandoRecebimentos",
]);

//==================================================================//
//============================= Dado ===============================//
//==================================================================//

const dado = ref({});

//==================================================================//
//================= Altera Controles Desativados ===================//
//==================================================================//

const alteraControlesDesativados = (update)=>{

    emits('update:alteraControlesDesativados',update);

};

//==================================================================//
//================= Altera Gerando Recebimentos ====================//
//==================================================================//

const alteraGerandoRecebimentos = (update) =>{

    emits('update:alteraGerandoRecebimentos',update);

};

//==================================================================//
//========================== Altera Dado ===========================//
//==================================================================//

const alteraDado = (update = {})=>{

    // Atualiza Dado
    dado.value = {
        ...dado.value,
        ...update
    }

    // Obtem os JobParcelas IDs utilizados.
    const jobParcelasSelecionados = new Set(
        dado.value.recebimentosNovos
            .map(recebimentoNovo => recebimentoNovo?.jobParcela?.id)
            .filter(Boolean)
    );

    // Atualiza os JobParcelas IDs utilizados.
    dado.value.jobParcelas = dado.value.jobParcelas.map((jobParcela)=>{

        return{
            ...jobParcela,
            disabled: jobParcelasSelecionados.has(jobParcela?.id)
        }

    });

};

//==================================================================//
//==================== Altera Recebimento Novo =====================//
//==================================================================//

const alteraRecebimentoNovo = (update)=>{

    // Se a Seleção for Falsa, resete os campos;
    if(update?.selecionado === false){
        update.autocalculado = false;
        update.jobParcela = {};
        update.contaFinanceira = {};
        update.porcentagemRecebimento = 0;
        update.valorRecebimento = 0;
    }

    let recebimentosNovos = dado?.value?.recebimentosNovos?.map((recebimentoNovo)=>{

        // Altere apenas o item esperado, ignore os outros.
        if(recebimentoNovo.id != update.id){
            return recebimentoNovo;
        }

        // Altera o item esperado.
        recebimentoNovo = {
            ...recebimentoNovo,
            ...update
        }

        // Calcula o Valor do Recebimento dele.
        recebimentoNovo.valorRecebimento = UtilNumber.getValueFromPercentage({
            porcentagem: UtilNumber.toNumber(recebimentoNovo?.porcentagemRecebimento),
            valor: UtilNumber.toNumber(recebimentoNovo?.jobParcela?.valorParaReceber)
        });

        return recebimentoNovo;

    });

    alteraDado({
        recebimentosNovos: recebimentosNovos
    });

};

//==================================================================//
//================ Autocalcula Recebimento Novo ====================//
//==================================================================//

const autocalculaRecebimentoNovo = (update)=>{

    let recebimentosNovos = dado?.value?.recebimentosNovos?.map((recebimentoNovo)=>{

        // Altere apenas o item esperado, ignore os outros.
        if(recebimentoNovo.id != update.id){
            return recebimentoNovo;
        }

        // Altera o item esperado.
        recebimentoNovo = {
            ...recebimentoNovo,
            ...update
        };

        // Se Autocalculado por desligado não autocalcule.
        if(!recebimentoNovo.autocalculado){
            return recebimentoNovo;
        }

        // Calcula a Porcentagem do Recebimento sem arredondar.
        let porcentagemRecebimento = UtilNumber.getPercentage({
            parte: recebimentoNovo?.contaAReceberValorParaReceber,
            total: recebimentoNovo?.jobParcela?.valorParaReceber,
            maximo: 100,
            arredondar: false,
        });

        // Calcula o Valor do Recebimento dele.
        recebimentoNovo.valorRecebimento = UtilNumber.getValueFromPercentage({
            porcentagem: porcentagemRecebimento,
            valor: recebimentoNovo?.jobParcela?.valorParaReceber
        });

        // Armazena a Porcentagem do Recebimento arredondando.
        recebimentoNovo.porcentagemRecebimento = porcentagemRecebimento;

        return recebimentoNovo;

    });

    alteraDado({
        recebimentosNovos: recebimentosNovos
    });

};

//==================================================================//
//==================== Valida Recebimento Novo =====================//
//==================================================================//

const validaRecebimentoNovo = (update)=>{

    let recebimentosNovos = dado?.value?.recebimentosNovos?.map((recebimentoNovo)=>{

        // Altere apenas o item esperado, ignore os outros.
        if(recebimentoNovo.id != update.id){
            return recebimentoNovo;
        }

        // Altera o item esperado.
        recebimentoNovo = {
            ...recebimentoNovo,
            ...update
        }

        return recebimentoNovo;

    });

    alteraDado({
        recebimentosNovos: recebimentosNovos
    });

};

//==================================================================//
//========================= Prepara Dado ===========================//
//==================================================================//

const preparaDado = (dado = {})=>{

    let recebimentosNovos = dado?.parcelas?.reduce((acc, parcela, index)=>{

        if(parcela?.contaAReceber?.status != "Prevista"){
            return acc;
        }

        acc.push({
            id: index,
            autocalculado: false,
            selecionado: false,
            valido: false,
            contaAReceberID: parcela?.contaAReceber?.id,
            contaAReceberCodigo: parcela?.contaAReceber?.codigo,
            contaAReceberValorOriginal: parcela?.contaAReceber?.valorOriginal,
            contaAReceberValorRecebido: parcela?.contaAReceber?.valorRecebido,
            contaAReceberValorParaReceber: parcela?.contaAReceber?.valorParaReceber,
            jobParcela: {},
            contaFinanceira: {},
            dataVencimento: parcela?.contaAReceber?.dataVencimento,
            dataVencimentoFormatada: parcela?.contaAReceber?.dataVencimentoFormatada,
            porcentagemRecebimento: 0,
            valorRecebimento: 0,
        });

        return acc;

    },[]);

    let jobParcelas = dado?.jobParcelas.map((jobParcela)=>{

        return{
            selecionado: false,
            id: jobParcela.id,
            codigo: jobParcela.codigo,
            sequencia: jobParcela.sequencia,
            descricao: jobParcela.descricao,
            tipoOculto: jobParcela.tipoOculto,
            tipo: jobParcela.tipo,
            valorOriginal: jobParcela?.valorOriginal,
            valorRecebido: jobParcela?.valorRecebido,
            valorParaReceber: jobParcela?.valorParaReceber,
            parcelaPorcentagem: jobParcela.parcelaPorcentagem,
        }

    });

    alteraDado({
        id: dado?.id,
        origem: dado?.origem,
        codigo: dado?.codigo,
        chaveAcesso: dado?.chaveAcesso,
        plugNotasID: dado?.plugNotasID,
        jobCodigo: dado?.job?.codigo,
        jobParcelas: jobParcelas,
        recebimentosNovos: recebimentosNovos
    });

};

//==================================================================//
//======================== Prepara Modal ===========================//
//==================================================================//

const preparaModal = ()=>{

    preparaDado({...props.dadoOriginal});

}

//==================================================================//
//==================== Watch => gerandoRecebimentos ================//
//==================================================================//

watch(()=> props.gerandoRecebimentos, () => {

    if(props.gerandoRecebimentos == true){
        preparaModal();
    }

},{ immediate: true });

</script>

<template>

    <main>

        <v-dialog
            content-class="c-dialog"
            v-model="props.gerandoRecebimentos"
            fullscreen
            transition="dialog-bottom-transition"
            persistent
        >

            <v-card class="c-modal">

                 <BarraNavegacao
                    :usuarioStore="props.usuarioStore"
                    :colaboradorStore="props.colaboradorStore"
                    :controlesDesativados="controlesDesativados"
                    :dado="dado"
                    @update:alteraControlesDesativados="alteraControlesDesativados"
                    @update:alteraGerandoRecebimentos="alteraGerandoRecebimentos"
                ></BarraNavegacao>

                <DadoConteudo
                    :controlesDesativados="props.controlesDesativados"
                    :gerandoRecebimentos="props.gerandoRecebimentos"
                    :dado="dado"
                    :contasFinanceiras="props.contasFinanceiras"
                    @update:alteraRecebimentoNovo="alteraRecebimentoNovo"
                    @update:autocalculaRecebimentoNovo="autocalculaRecebimentoNovo"
                    @update:validaRecebimentoNovo="validaRecebimentoNovo"
                ></DadoConteudo>
            
            </v-card>

        </v-dialog>

    </main>

</template>

<style scoped>

.v-dialog__content >>> .v-dialog{
    background-color: white;
}

</style>